// §6.3 — the Dock: bottom, 64px, magnification on hover, running indicators, and a Trash.
//
// **Magnification is the one piece of chrome in this project that is genuinely about feel**, so it
// is worth saying what it is: hovering an icon scales it, and scales its *neighbours* by less, with
// the falloff a function of distance. One hovered index drives the whole row, which is why the
// scale is computed here and handed to CSS as a custom property rather than being eight hover
// rules that cannot see each other.
//
// The row is: pinned apps, then any running app that is not pinned, then a separator, then Trash.
// A running app shows a dot under it whether or not it is pinned — that is what the dot is for.
import { useState } from 'react';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import type { Win } from '@hangar/shared';

/** How many apps the Dock pins. Beyond this, the launcher is one gesture away. */
/**
 * §6.3 (D-510) — the Dock's default set, named rather than derived.
 *
 * It used to be `launchable.slice(0, 8)`, which meant "whichever eight apps sort first" — so
 * registering **Archive** silently pushed Notepad out of the Dock, and five more Workbench
 * utilities would have taken Explorer, Settings and Terminal with it. John chose these twelve, in
 * this order. An id that is not installed, or not visible to this session (§24.11 — Chat,
 * Connections and Reminders are signed-in only), is simply skipped; a running app that is not on
 * the list still joins the Dock while it runs, exactly as before.
 */
export const PINNED_IDS: readonly string[] = [
  'alarms',
  'browser',
  'calculator',
  'chat',
  'clock',
  'connections',
  'explorer',
  'notepad',
  'photos',
  'reminders',
  'terminal',
  'settings',
];

/** The hovered icon's scale, and how far the effect reaches. */
const MAX_SCALE = 1.6;
const REACH = 2.6;

/**
 * The scale for an icon `distance` places from the hovered one.
 *
 * A cosine falloff rather than a linear one: linear leaves a visible crease at the edge of the
 * effect, and the whole point of the magnifier is that the row reads as one curve.
 */
export function magnification(distance: number): number {
  if (distance >= REACH) return 1;
  const t = distance / REACH;
  return 1 + (MAX_SCALE - 1) * (Math.cos(t * Math.PI) + 1) * 0.5;
}

interface DockApp {
  id: string;
  name: string;
  running: boolean;
}

export function Dock({
  windows,
  focusedId,
  unhealthy,
  badges,
}: {
  windows: readonly Win[];
  focusedId: string | null;
  unhealthy: ReadonlySet<string>;
  badges: ReadonlyMap<string, number>;
}) {
  const kernel = useKernel();
  const [hovered, setHovered] = useState<number | null>(null);

  const running = new Set(windows.map((win) => win.appId));
  const launchable = kernel.launch.launchable();
  // Ordered by the list, not by the registry: `launchable` decides *whether* an app appears,
  // `PINNED_IDS` decides where.
  const byId = new Map(launchable.map((app) => [app.id, app]));
  const pinned = PINNED_IDS.map((id) => byId.get(id)).filter((app) => app !== undefined);
  const pinnedIds = new Set(pinned.map((app) => app.id));

  const apps: DockApp[] = [
    ...pinned.map((app) => ({ id: app.id, name: app.name, running: running.has(app.id) })),
    // A running app that is not pinned still belongs in the Dock while it is running.
    ...[...running]
      .filter((id) => !pinnedIds.has(id))
      .map((id) => ({ id, name: kernel.appName(id), running: true })),
  ];

  /** Click an app: launch it, or bring its windows forward, or hide them if they are already up. */
  const activate = (appId: string): void => {
    const mine = windows.filter((win) => win.appId === appId);
    if (mine.length === 0) {
      kernel.launch.launch(appId);
      return;
    }
    const front = mine.find((win) => win.id === focusedId && win.state !== 'minimized');
    if (front) {
      for (const win of mine) kernel.wm.minimize(win.id);
      return;
    }
    for (const win of mine) {
      if (win.state === 'minimized') kernel.wm.restore(win.id);
    }
    const last = mine[mine.length - 1];
    if (last) kernel.wm.focus(last.id);
  };

  /** The Trash sits after a separator, and the separator is the only thing that is not an app. */
  const trashIndex = apps.length;

  const scaleOf = (index: number): number =>
    hovered === null ? 1 : magnification(Math.abs(index - hovered));

  return (
    <div className="macos-dock-layer" data-testid="dock-layer">
      <div
        className="macos-dock"
        data-testid="dock"
        onPointerLeave={() => setHovered(null)}
      >
        {apps.map((app, index) => (
          <button
            key={app.id}
            type="button"
            className="macos-dock-item"
            data-testid={`bar-entry-${app.id}`}
            data-scale={scaleOf(index).toFixed(3)}
            data-running={String(app.running)}
            // §9.6 — three consecutive misses and the icon dims to 50 %.
            data-health={unhealthy.has(app.id) ? 'unhealthy' : 'ok'}
            style={{ ['--dock-scale' as string]: scaleOf(index) }}
            title={app.name}
            aria-label={app.name}
            onPointerEnter={() => setHovered(index)}
            onClick={() => activate(app.id)}
          >
            <span
              className="macos-dock-icon"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(app.id, 48) }}
            />
            {(badges.get(app.id) ?? 0) > 0 && (
              <span
                className="macos-dock-badge"
                data-testid={`bar-badge-${app.id}`}
                aria-label={COPY.chrome.notifications}
              >
                {badges.get(app.id)}
              </span>
            )}
            {/* §6.3's running indicator: a dot under the icon, and nothing else. */}
            <span
              className="macos-dock-dot"
              data-testid={`bar-indicator-${app.id}`}
              data-state={app.running ? 'running' : 'idle'}
              aria-hidden
            />
          </button>
        ))}

        <span className="macos-dock-separator" aria-hidden />

        <button
          type="button"
          className="macos-dock-item macos-dock-trash"
          data-testid="dock-trash"
          data-scale={scaleOf(trashIndex).toFixed(3)}
          style={{ ['--dock-scale' as string]: scaleOf(trashIndex) }}
          title={COPY.explorer.recycleBin}
          aria-label={COPY.explorer.recycleBin}
          onPointerEnter={() => setHovered(trashIndex)}
          // §11.11 — "dock item on macOS". Since M13.6 the Recycle Bin is an app of its own, so
          // this opens it rather than the File Explorer it used to live inside (D-145).
          onClick={() => kernel.launch.launch('recycle-bin')}
        >
          <span
            className="macos-dock-icon"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: kernel.appIconSvg('recycle-bin', 48) }}
          />
        </button>
      </div>
    </div>
  );
}
